import { useState } from "react";
import { motion } from "motion/react";
import { useExtracted, useLocale } from "next-intl";
import type { Duration } from "@/lib/pricing";
import { useHasPermanentAccess } from "@/hooks/useAuth";
import { useEventListener } from "@/hooks/useEventBus";
import { useModalRouter } from "@/hooks/useModalRouter";
import { usePricingOffer } from "@/hooks/usePricingOffer";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { DurationSelector } from "@/components/purchase/DurationSelector";
import { useMembershipFeatures } from "@/components/purchase/features";
import { PricingFeatureList } from "@/components/purchase/PricingFeatureList";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Logotype } from "@/components/ui/logotype";

export function UpgradeModal() {
  const te = useExtracted();
  const locale = useLocale();
  const hasPermanentAccess = useHasPermanentAccess();
  const { start } = useStripeCheckout();
  const { modal, openModal, setOpen } = useModalRouter();
  const open = modal === "upgrade";
  const [selected, setSelected] = useState<Duration>("1y");
  const [loading, setLoading] = useState(false);
  const membershipFeatures = useMembershipFeatures();
  const offer = usePricingOffer();

  useEventListener("upgrade:open", () => openModal("upgrade"));

  const REDIRECTING_LABEL = te("Redirecting...");
  const PURCHASE_LABEL = te("Unlock 3.0 Flagship");

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await start({
        duration: selected,
        locale: locale.startsWith("zh") ? "zh" : "en",
      });
    } catch {
      setLoading(false);
    }
  };

  if (hasPermanentAccess) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => setOpen("upgrade", next)}>
      <DialogContent className="gap-6 sm:max-w-md" showCloseButton={false}>
        <DialogTitle>{te("3.0 Flagship")}</DialogTitle>
        <p className="text-sm text-muted-foreground">
          {te("Auto-renews. Cancel anytime.")}
        </p>

        <div className="flex items-center justify-center">
          <Logotype size="md" />
        </div>

        <DurationSelector
          offer={offer}
          selected={selected}
          onSelect={setSelected}
        />

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface border-border-subtle w-full space-y-4 rounded-md border p-5"
          initial={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
        >
          <PricingFeatureList
            features={membershipFeatures}
            staggerDelay={0.4}
          />

          <Button
            className="h-10 w-full rounded text-base hover:brightness-125"
            disabled={loading}
            variant="interactive"
            onClick={handleSubmit}
          >
            {loading ? REDIRECTING_LABEL : PURCHASE_LABEL}
          </Button>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
